import React from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { useI18n } from "../../i18n";

const Privacy = () => {
  const { lang, t } = useI18n();
  return (
    <>
      <Header />
      
      <section className="pt-36 pb-24 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="rise font-display text-5xl md:text-6xl leading-[1.05] mb-4 text-ink">
            {lang === "en" ? "Privacy Policy" : t("footer.legal.privacy")}
          </h1>
          {lang !== "en" && (
            <p className="mb-4 rounded-2xl border border-line bg-surface px-4 py-3 text-sm text-ink-2">{t("legal.notice")}</p>
          )}
          <p className="text-ink-3 text-sm mb-12 pb-8 border-b border-line">
            Last Updated: December 2024
          </p>

          <div className="legal" dir="ltr" lang="en">
            {/* Legal Framework */}
            <section>
              <div className="rounded-lg bg-surface border border-line p-6 mb-6">
                <p className="text-base leading-relaxed mb-4">
                  This document is an electronic record in terms of Information Technology Act, 2000 and rules there under as applicable and the amended provisions pertaining to electronic records in various statutes as amended by the Information Technology Act, 2000. This electronic record is generated by a computer system and does not require any physical or digital signatures.
                </p>
                <p className="text-base leading-relaxed">
                  This document is published in accordance with the provisions of Rule 3 (1) of the Information Technology (Intermediaries guidelines) Rules, 2011 that require publishing the rules and regulations, privacy policy and Terms of Use for access or usage of domain name optivratech.com.
                </p>
              </div>
            </section>

            {/* Introduction */}
            <section>
              <h2>Introduction</h2>
              <p className="text-base leading-relaxed mb-4">
                This Privacy Policy describes how <strong>Optivra Tech</strong> and its affiliates (collectively "Optivra Tech, we, our, us") collect, use, share, protect or otherwise process your information/personal data through our website optivratech.com (hereinafter referred to as Platform).
              </p>
              <p className="text-base leading-relaxed mb-4">
                The Platform is owned by <strong>Optivra Tech</strong>, a company incorporated under the Companies Act, 1956 with its registered office at <strong>C-52, Amar Colony, Udasar Fanta, Jaipur Road, Bikaner, Rajasthan, India</strong> (hereinafter referred to as 'Platform Owner', 'we', 'us', 'our').
              </p>
              <p className="text-base leading-relaxed mb-4">
                Please note that you may be able to browse certain sections of the Platform without registering with us. We do not offer any product/service under this Platform outside India and your personal data will primarily be stored and processed in India.
              </p>
              <p className="text-base leading-relaxed">
                <strong>By visiting this Platform, providing your information or availing any product/service offered on the Platform, you expressly agree to be bound by the terms and conditions of this Privacy Policy, the Terms of Use and the applicable service/product terms and conditions.</strong> If you do not agree, please do not use or access our Platform.
              </p>
            </section>

            {/* Collection */}
            <section>
              <h2>1. Information Collection</h2>
              <p className="text-base leading-relaxed mb-4">
                We collect your personal data when you use our Platform, services or otherwise interact with us during the course of our relationship. Some of the information that we may collect includes but is not limited to:
              </p>
              
              <h3>Personal Information</h3>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Name, date of birth, and age</li>
                <li>Address (residential and business)</li>
                <li>Telephone/mobile number</li>
                <li>Email ID</li>
                <li>Proof of identity or address documents</li>
                <li>Payment information (bank account, credit/debit card details)</li>
                <li>Transaction history and behavior on the Platform</li>
                <li>Preferences and choices</li>
              </ul>

              <h3>Sensitive Personal Data</h3>
              <p className="text-base leading-relaxed mb-4">
                Some sensitive personal data may be collected with your consent, such as:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Bank account or payment instrument information</li>
                <li>Biometric information (when opted for specific features)</li>
                <li>Financial information as required for services</li>
              </ul>

              <p className="text-base leading-relaxed mb-4">
                <strong>You always have the option to not provide information</strong>, by choosing not to use a particular service or feature on the Platform.
              </p>

              <div className="rounded-lg bg-brand-soft border border-brand/20 p-6 mt-4">
                <p className="text-base leading-relaxed">
                  <strong>Security Alert:</strong> If you receive an email or call from a person/association claiming to be Optivra Tech seeking personal data like debit/credit card PIN, net-banking or mobile banking password, never provide such information. If you have already revealed such information, report it immediately to an appropriate law enforcement agency.
                </p>
              </div>
            </section>

            {/* Usage */}
            <section>
              <h2>2. How We Use Your Information</h2>
              <p className="text-base leading-relaxed mb-4">
                We use personal data to provide the services you request. To the extent we use your personal data to market to you, we will provide you the ability to opt-out of such uses. We use your personal data for:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Providing services and products you request</li>
                <li>Handling and fulfilling orders</li>
                <li>Enhancing customer experience</li>
                <li>Resolving disputes and troubleshooting problems</li>
                <li>Informing you about online and offline offers, products, services, and updates</li>
                <li>Customizing your experience on the Platform</li>
                <li>Detecting and protecting against error, fraud and criminal activity</li>
                <li>Enforcing our terms and conditions</li>
                <li>Conducting marketing research, analysis and surveys</li>
                <li>Processing payments and transactions</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            {/* Sharing */}
            <section>
              <h2>3. Information Sharing</h2>
              
              <h3>Internal Sharing</h3>
              <p className="text-base leading-relaxed mb-4">
                We may share your personal data internally within our group entities, other corporate entities, and affiliates to provide you access to the services and products offered by them. These entities and affiliates may market to you as a result of such sharing unless you explicitly opt-out.
              </p>

              <h3>Third-Party Sharing</h3>
              <p className="text-base leading-relaxed mb-4">
                We may disclose personal data to third parties such as:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Service providers and business partners</li>
                <li>Logistics and delivery partners</li>
                <li>Payment processors and financial institutions</li>
                <li>Third-party reward programs</li>
                <li>Marketing and advertising partners</li>
                <li>Technology service providers</li>
              </ul>

              <h3>Legal Disclosure</h3>
              <p className="text-base leading-relaxed mb-4">
                We may disclose personal and sensitive personal data to:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Government agencies or authorized law enforcement agencies if required by law</li>
                <li>Respond to subpoenas, court orders, or other legal processes</li>
                <li>Enforce our Terms of Use or Privacy Policy</li>
                <li>Respond to claims of rights violations</li>
                <li>Protect the rights, property or personal safety of our users or the general public</li>
                <li>Prevent, detect, mitigate, and investigate fraudulent or illegal activities</li>
              </ul>
            </section>

            {/* Security */}
            <section>
              <h2>4. Security Precautions</h2>
              <p className="text-base leading-relaxed mb-4">
                To protect your personal data from unauthorized access or disclosure, loss or misuse, we adopt reasonable security practices and procedures in line with industry standards.
              </p>
              <p className="text-base leading-relaxed mb-4">
                Once your information is in our possession or whenever you access your account information, we adhere to our security guidelines to protect it against unauthorized access and offer the use of a secure server.
              </p>
              <p className="text-base leading-relaxed mb-4">
                <strong>Important:</strong> However, the transmission of information over the internet is not completely secure for reasons beyond our control. By using the Platform, you accept the security implications of data transmission over the internet which cannot always be guaranteed as completely secure.
              </p>
              <p className="text-base leading-relaxed">
                <strong>Your Responsibility:</strong> You are responsible for ensuring the protection of login and password records for your account. Never share your credentials with anyone.
              </p>
            </section>

            {/* Data Retention and Deletion */}
            <section>
              <h2>5. Data Deletion and Retention</h2>
              
              <h3>Account Deletion</h3>
              <p className="text-base leading-relaxed mb-4">
                You have an option to delete your account by visiting your profile and settings on our Platform. This action would result in you losing all information related to your account. You may also write to us at the contact information provided below to assist you with these requests.
              </p>
              <p className="text-base leading-relaxed mb-4">
                We may, in the event of any pending grievance, claims, pending services, refuse or delay deletion of the account. Once the account is deleted, you will lose access to all account-related information.
              </p>

              <h3>Data Retention</h3>
              <p className="text-base leading-relaxed mb-4">
                We retain your personal data information for a period no longer than is required for the purpose for which it was collected or as required under any applicable law.
              </p>
              <p className="text-base leading-relaxed">
                However, we may retain data related to you if we believe it may be necessary to prevent fraud or future abuse or for other legitimate purposes. We may continue to retain your data in anonymized form for analytical and research purposes.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2>6. Your Rights</h2>
              <p className="text-base leading-relaxed mb-4">
                You have the following rights regarding your personal data:
              </p>
              <ul className="space-y-2 ml-4">
                <li><strong>Access:</strong> You may access your personal data directly through the functionalities provided on the Platform</li>
                <li><strong>Rectification:</strong> You can update and correct your personal data</li>
                <li><strong>Deletion:</strong> You can request deletion of your account and personal data</li>
                <li><strong>Opt-out:</strong> You can opt-out of marketing communications</li>
                <li><strong>Withdrawal of Consent:</strong> You can withdraw consent for data processing</li>
              </ul>
            </section>

            {/* Consent */}
            <section>
              <h2>7. Consent</h2>
              <p className="text-base leading-relaxed mb-4">
                By visiting our Platform or by providing your information, you consent to the collection, use, storage, disclosure and otherwise processing of your information on the Platform in accordance with this Privacy Policy.
              </p>
              <p className="text-base leading-relaxed mb-4">
                If you disclose to us any personal data relating to other people, you represent that you have the authority to do so and permit us to use the information in accordance with this Privacy Policy.
              </p>
              <p className="text-base leading-relaxed mb-4">
                You, while providing your personal data over the Platform, consent to us (including our other corporate entities, affiliates, partners, and third parties) to contact you through SMS, instant messaging apps, call and/or e-mail for the purposes specified in this Privacy Policy.
              </p>

              <h3>Withdrawal of Consent</h3>
              <p className="text-base leading-relaxed mb-4">
                You have an option to withdraw your consent by writing to the Grievance Officer at the contact information provided below. Please mention "Withdrawal of consent for processing personal data" in your subject line.
              </p>
              <p className="text-base leading-relaxed">
                Please note that your withdrawal of consent will not be retrospective and will be in accordance with the Terms of Use, this Privacy Policy, and applicable laws. We reserve the right to restrict or deny the provision of our services for which we consider such information to be necessary.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2>8. Cookies and Tracking Technologies</h2>
              <p className="text-base leading-relaxed mb-4">
                We use cookies and similar tracking technologies to track activity on our Platform and store certain information. Cookies are files with small amount of data which may include an anonymous unique identifier.
              </p>
              <p className="text-base leading-relaxed">
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Platform.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2>9. Changes to this Privacy Policy</h2>
              <p className="text-base leading-relaxed mb-4">
                Please check our Privacy Policy periodically for changes. We may update this Privacy Policy to reflect changes to our information practices.
              </p>
              <p className="text-base leading-relaxed">
                We may alert/notify you about significant changes to the Privacy Policy, in the manner as may be required under applicable laws.
              </p>
            </section>

            {/* Grievance Officer */}
            <section>
              <h2>10. Grievance Officer</h2>
              <p className="text-base leading-relaxed mb-4">
                In accordance with Information Technology Act, 2000 and rules made thereunder, the contact details of the Grievance Officer are provided below:
              </p>
              <div className="rounded-lg bg-surface border border-line p-6 mt-4">
                <p className="text-base mb-2"><strong>Company Name:</strong> Optivra Tech</p>
                <p className="text-base mb-2"><strong>Registered Office:</strong> C-52, Amar Colony, Udasar Fanta, Jaipur Road, Bikaner, Rajasthan, India</p>
                <p className="text-base mb-2"><strong>GST Number:</strong> 08BNOPS7805R1ZT</p>
                <p className="text-base mb-2"><strong>Email:</strong> founder@optivratech.com</p>
                <p className="text-base mb-2"><strong>Phone:</strong> +91 86198 38718</p>
                <p className="text-base"><strong>Business Hours:</strong> Monday - Saturday (9:00 AM - 6:00 PM IST)</p>
              </div>
            </section>

            {/* Your Obligations */}
            <section>
              <h2>11. Your Obligations</h2>
              <p className="text-base leading-relaxed mb-4">
                By using the Platform, you agree to:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Provide true, accurate and complete information during and after registration</li>
                <li>Be responsible for all acts done through your registered account</li>
                <li>Independently assess that the Services meet your requirements</li>
                <li>Not use the Platform for any unlawful, illegal or forbidden purpose</li>
                <li>Protect your login credentials and account information</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
            </section>

            {/* Governing Law */}
            <section>
              <h2>12. Governing Law and Jurisdiction</h2>
              <p className="text-base leading-relaxed mb-4">
                This Privacy Policy and any dispute or claim relating to it shall be governed by and construed in accordance with the laws of India.
              </p>
              <p className="text-base leading-relaxed">
                All disputes arising out of or in connection with this Privacy Policy shall be subject to the exclusive jurisdiction of the courts in Bikaner, Rajasthan, India.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2>13. Contact Us</h2>
              <p className="text-base leading-relaxed mb-2">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="rounded-lg bg-surface border border-line p-6 mt-4">
                <p className="text-base mb-2"><strong>Optivra Tech</strong></p>
                <p className="text-base mb-2">C-52, Amar Colony, Udasar Fanta, Jaipur Road, Bikaner, Rajasthan, India</p>
                <p className="text-base mb-2">Email: founder@optivratech.com</p>
                <p className="text-base mb-2">Phone: +91 86198 38718</p>
                <p className="text-base">GST: 08BNOPS7805R1ZT</p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="mt-8">
              <div className="rounded-lg bg-brand-soft border border-brand/20 p-6">
                <p className="text-base leading-relaxed">
                  <strong>By using our Platform, you acknowledge that you have read and understood this Privacy Policy and agree to be bound by its terms.</strong>
                </p>
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Privacy;
